/** Light-theme map palette — Vietnam-focused digital GIS. */
export const MAP_COLORS = {
  vietnamFill: '#DDEEFF',
  vietnamBorder: '#2388FF',
  vietnamBorderSoft: '#58A9FF',
  vietnamBorderGlow: '#2E8FFF',
  provinceBorder: '#8FC6FF',
  selectedProvinceFill: '#5DA9FF',
  districtBorder: '#A9C9E8',
  districtLabel: '#6B849C',
  placeLabel: '#56738F',
  calloutBorder: '#D7E0EA',
  sea: '#EAF7FF',
  healthy: '#23B95F',
  warning: '#FF9800',
  critical: '#FF3B30',
  inactive: '#8A97A8',
  hullFill: 'rgba(35, 136, 255, 0.08)',
  hullBorder: '#2388FF',
} as const;

export const MAP_MARKER_STATUS_COLORS: Record<string, string> = {
  healthy: MAP_COLORS.healthy,
  warning: MAP_COLORS.warning,
  critical: MAP_COLORS.critical,
  inactive: MAP_COLORS.inactive,
};

export const MAP_GEOJSON_URLS = {
  vietnamBoundary: '/geojson/vietnam-boundary.geojson',
  vietnamProvinces: '/geojson/vietnam-provinces.geojson',
  vietnamDistricts: '/geojson/vietnam-districts.geojson',
} as const;

/** National Vietnam mainland bounds [west, south, east, north]. */
export const VIETNAM_NATIONAL_BOUNDS: [[number, number], [number, number]] = [
  [102.15, 8.4],
  [109.5, 23.4],
];

export const VIETNAM_NATIONAL_CENTER = {
  longitude: 105.85,
  latitude: 16.0,
} as const;

/** Max zoom when fitting full Vietnam — allow fill inside wrapper. */
export const VIETNAM_NATIONAL_MAX_ZOOM = 6.4;

export const CAP_DON_VI = {
  CONG_TY: 1,
  DON_VI: 2,
} as const;

/**
 * National fit — modest card gutters so Vietnam is not over-zoomed-out.
 * Bottom reserve for legend.
 */
export const MAP_NATIONAL_FIT_PADDING = {
  top: 24,
  right: 148,
  bottom: 52,
  left: 148,
} as const;

/**
 * Padding when fitting marker / province subsets.
 */
export const MAP_FIT_BOUNDS_PADDING = {
  top: 40,
  right: 148,
  bottom: 52,
  left: 148,
} as const;

/**
 * Compact callout dimensions — name + inventory label; expand on interaction.
 */
export const MAP_CALLOUT = {
  width: 132,
  height: 52,
  selectedWidth: 152,
  selectedHeight: 64,
  hoverWidth: 152,
  hoverHeight: 56,
  labelOnlyWidth: 110,
  labelOnlyHeight: 28,
  /** Fixed left/right lane inset from viewport edges. */
  laneInset: 12,
  gap: 8,
  viewportPadding: 10,
  maxAttempts: 28,
  elbowOffset: 20,
  /** Vertical stagger step inside a lane when cards collide. */
  staggerStep: 8,
} as const;

export type CalloutMode =
  | 'full'
  | 'compact'
  | 'label-only'
  | 'selected-only';

/** Density thresholds for adaptive callout rendering. */
export const MAP_CALLOUT_MODE_THRESHOLDS = {
  full: 5,
  compact: 12,
  labelOnly: 30,
} as const;

/** @deprecated use MAP_CALLOUT_MODE_THRESHOLDS */
export const MAP_CALLOUT_LIMITS = {
  showAll: MAP_CALLOUT_MODE_THRESHOLDS.compact,
  priorityOnly: MAP_CALLOUT_MODE_THRESHOLDS.labelOnly,
} as const;

/** Small status markers — primary visual focus. */
export const MAP_MARKER_SIZES = {
  coreRadius: 4.5,
  glowRadius: 9,
  glowRadiusCritical: 11,
  glowRadiusSelected: 12,
  strokeWidth: 1.75,
  leaderWidth: 1,
  leaderWidthHover: 1.5,
  leaderWidthSelected: 2,
  /** @deprecated use leaderWidthHover / leaderWidthSelected */
  leaderWidthActive: 2,
  leaderAlpha: 140,
  leaderAlphaHover: 230,
  leaderAlphaSelected: 255,
} as const;

export const MAP_WAREHOUSE_HIGHLIGHT = {
  singleRadiusKm: 15,
  bufferKm: 10,
} as const;
